import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PosDeviceContext } from '../context/PosDeviceContext';
import './Navbar.css';

const NavDropdown = ({ label, items, isOpen, onToggle, onClose, isActive, linkClass }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const groupActive = items.some((item) => isActive(item.to));

  return (
    <div className="navbar-dropdown" ref={ref}>
      <button
        type="button"
        className={`navbar-link navbar-dropdown-trigger${groupActive ? ' is-active' : ''}${isOpen ? ' is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        {label}
        <span className="navbar-dropdown-caret" aria-hidden>▾</span>
      </button>
      {isOpen && (
        <div className="navbar-dropdown-panel" role="menu">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              className={linkClass(item.to, 'navbar-dropdown-link')}
              onClick={onClose}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const { enrolled: posDeviceEnrolled } = useContext(PosDeviceContext);
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    setDrawerOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  if (!user) return null;

  const primary = [];
  primary.push({ to: '/orders', label: 'Orders' });
  if (user.role !== 'Cashier' && user.role !== 'Order Creator') {
    primary.push({ to: '/catering-planner', label: 'CaterCraft' });
  }
  if (['Order Creator', 'Admin', 'Cashier', 'Franchise Admin', 'Store Manager', 'Accountant'].includes(user.role)) {
    primary.push({ to: '/orders/create', label: 'Create order' });
  }
  if (['Store Manager', 'Admin', 'Franchise Admin'].includes(user.role)) {
    primary.push({ to: '/store-manager', label: 'Store manager' });
  }
  if (posDeviceEnrolled && ['Admin', 'Store Manager', 'Cashier', 'Franchise Admin'].includes(user.role)) {
    primary.push({ to: '/pos', label: 'POS & CRM' });
  }
  if (user.role === 'Admin') {
    primary.push({ to: '/owner', label: 'Owner' });
  }
  if (['Accountant', 'Admin'].includes(user.role)) {
    primary.push({ to: '/accountant', label: 'Accountant' });
    primary.push({ to: '/caterers', label: 'Caterers SOA' });
  }

  const catalog = [];
  if (['Order Creator', 'Admin'].includes(user.role)) {
    catalog.push(
      { to: '/agents', label: 'Agents' },
      { to: '/desserts', label: 'Desserts' },
      { to: '/cup-cakes', label: 'Cup cakes' },
      { to: '/plants', label: 'Plants' },
      { to: '/flavours', label: 'Flavours' },
      { to: '/catering-menu', label: 'Menu items' }
    );
    if (user.role === 'Admin') {
      catalog.push({ to: '/grazing-tables', label: 'Grazing tables' });
    }
  }

  const admin = [];
  if (user.role === 'Admin') {
    admin.push(
      { to: '/analytics', label: 'Analytics' },
      { to: '/users', label: 'Users' },
      { to: '/pos-menus', label: 'POS menus' }
    );
  }

  const allLinks = [...primary, ...catalog, ...admin];
  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  const storeNavLabel =
    user?.assigned_stores?.length === 1
      ? user.assigned_stores[0].name
      : user?.assigned_stores?.length > 1
        ? user.assigned_stores.map((s) => s.name).join(' · ')
        : null;

  const linkClass = (path, extra = '') =>
    `navbar-link${isActive(path) ? ' is-active' : ''}${extra ? ` ${extra}` : ''}`;

  const toggleMenu = (key) => setOpenMenu((cur) => (cur === key ? null : key));
  const closeMenu = () => setOpenMenu(null);

  return (
    <nav className="navbar" aria-label="Main">
      <div className="navbar-shell">
        <div className="navbar-row navbar-row--top">
          <Link to="/" className="navbar-brand" aria-label="Home">
            <img src="/cakexpress-logo.png" alt="" className="navbar-logo" />
            <span className="navbar-brand-text">CakeXpress</span>
          </Link>

          <div className="navbar-desktop-primary">
            {primary.map((item) => (
              <Link key={item.to} to={item.to} className={linkClass(item.to)}>
                {item.label}
              </Link>
            ))}
            {catalog.length > 0 && (
              <NavDropdown
                label="Catalog"
                items={catalog}
                isOpen={openMenu === 'catalog'}
                onToggle={() => toggleMenu('catalog')}
                onClose={closeMenu}
                isActive={isActive}
                linkClass={linkClass}
              />
            )}
            {admin.length > 0 && (
              <NavDropdown
                label="Admin"
                items={admin}
                isOpen={openMenu === 'admin'}
                onToggle={() => toggleMenu('admin')}
                onClose={closeMenu}
                isActive={isActive}
                linkClass={linkClass}
              />
            )}
          </div>

          <div className="navbar-end">
            <button
              type="button"
              className="navbar-burger"
              aria-expanded={drawerOpen}
              aria-controls="navbar-drawer"
              onClick={() => setDrawerOpen((o) => !o)}
            >
              <span className="navbar-burger-lines" aria-hidden>
                <span />
                <span />
                <span />
              </span>
              <span className="navbar-burger-label">Menu</span>
            </button>
            <div className="navbar-user-actions">
              {storeNavLabel ? (
                <span className="navbar-store-pill" title={storeNavLabel}>
                  {storeNavLabel}
                </span>
              ) : null}
              <span className="navbar-user" title={user.email || user.username}>
                {user.first_name || user.username}
              </span>
              <button type="button" onClick={logout} className="btn btn-secondary btn-nav-logout">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <>
          <button type="button" className="navbar-backdrop" aria-label="Close menu" onClick={() => setDrawerOpen(false)} />
          <div id="navbar-drawer" className="navbar-drawer" role="dialog" aria-modal="true">
            <div className="navbar-drawer-head">
              <span className="navbar-drawer-title">Navigate</span>
              <button type="button" className="navbar-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="navbar-drawer-body">
              {allLinks.map((item) => (
                <Link key={item.to} to={item.to} className={linkClass(item.to, 'navbar-drawer-link')}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
